import { capSQLiteValues, SQLiteDBConnection } from '@capacitor-community/sqlite';
import { useIonAlert } from '@ionic/react';
import { Drivers, Storage } from '@ionic/storage';
import { createContext, ReactNode, useEffect, useState } from 'react';
import { SQLiteHook, useSQLite } from 'react-sqlite-hook/dist';
import { BehaviorSubject, filter, firstValueFrom, take } from 'rxjs';

import { logConsole, logTransaction } from '../common/log';
import { delay } from '../common/util';
import { LoadingMessage } from '../components/loadingMessage/LoadingMessage';
import { LogType } from '../models/log.model';
import { AppDispatch } from '../store';
import { useAppDispatch } from '../store/hooks';
import { setTransactionLogSizeState } from '../store/logSlice';
import { ConfigName, loadConfig, selectConfigString, updateConfigBool } from './common/config';
import upgradeVersion0000 from './upgrade/version0000';
import upgradeVersion0001 from './upgrade/version0001';
import upgradeVersion0002 from './upgrade/version0002';
import upgradeVersion0003 from './upgrade/version0003';
import upgradeVersion0004 from './upgrade/version0004';

// For information on how this was created, see the following
// https://github.com/capacitor-community/sqlite#readme
// https://github.com/capacitor-community/sqlite/blob/master/docs/Ionic-React-Usage.md

let runOnce = 0;

// Database Name
export let dbName: string = process.env.REACT_APP_DBNAME!;
// Singleton SQLite Hook
export let sqlite: SQLiteHook;
// Is DB working?
export let isUsingSQLiteRef: boolean;
// Reference to storage object
export let storage: Storage | null = null;

// Manage concurrent SQL queries
let runQuerySubject: BehaviorSubject<any> = new BehaviorSubject<number>(0);
let runQueryNumber: number = 0;
let runQueriesInQueue: number = 0;

let lastConnectionLog = '';

export const sqlConnectionProvider = {
  getConnection: async () => {
    let connectionMessage = ['GET CONNECTION'];
    let db: SQLiteDBConnection | undefined;
    let res = false;

    const retrieveConnection = async () => {
      let success = false;
      try {
        connectionMessage.push('Try retrieve connection');
        db = await sqlite.retrieveConnection(dbName);
        connectionMessage.push('Connection retrieved');
        success = true;
      } catch (ex: any) {
        const error = ex?.message || ex;
        connectionMessage.push('Failed to retrieve connection');
        connectionMessage.push(error);
        success = false;
      } finally {
        return success;
      }
    };

    const createConnection = async () => {
      let success = false;
      try {
        connectionMessage.push('Try create connection');
        db = await sqlite.createConnection(dbName, false, 'no-encryption', 1);
        connectionMessage.push('Connection created');
        success = true;
      } catch (ex: any) {
        const error = ex?.message || ex;
        connectionMessage.push('Failed to create connection');
        connectionMessage.push(error);
        success = false;
        // If create connection fails, check if fail is valid
        if (error === `CreateConnection: Connection ${dbName} already exists`) {
          connectionMessage.push('Error says that connection exists!');
          // Error says that connection already available...something is wrong...close all connections and start over
          try {
            connectionMessage.push('Closing all connections...');
            await sqlite.closeAllConnections();
            connectionMessage.push('Connections closed');

            connectionMessage.push('Checking connection consistency');
            const consistancyRes = await sqlite.checkConnectionsConsistency();
            connectionMessage.push(`Consistency result: ${consistancyRes.result ? 'consistent' : 'inconsistent'}`);
            connectionMessage.push(`Consistency message: ${consistancyRes.message}`);
            connectionMessage.push('Try re-create connection');

            await delay(1000);

            db = await sqlite.createConnection(dbName, false, 'no-encryption', 1);
            connectionMessage.push('Connection created');
            success = true;
          } catch (ex1: any) {
            const error = ex1?.message || ex1;
            connectionMessage.push('Failed to re-create connection');
            connectionMessage.push(error);
            success = false;
          }
        }
      } finally {
        return success;
      }
    };

    try {
      // Check if connection exists
      let isConnected = await sqlite.isConnection(dbName);
      if (isConnected.result) {
        connectionMessage.push('Sqlite already connected');
        // If connection exists, retrieve connection
        connectionMessage.push('Attempting to retrieve connection from database');
        res = await retrieveConnection();
        if (!res) {
          // If failed to retrieve, create connection
          connectionMessage.push('Failed to retrieve connection to database');
          connectionMessage.push('Attempting to create connection to database');
          res = await createConnection();
          if (!res) {
            connectionMessage.push('Failed to create connection to database');
          }
        }
      } else {
        connectionMessage.push('Sqlite is not connected');
        // If connection does not exist, create connection
        connectionMessage.push('Attempting to create connection to database');
        res = await createConnection();
        if (!res) {
          // If failed to create, retrieve connection
          connectionMessage.push('Failed to create connection to database');
          connectionMessage.push('Attempting to retrieve connection to database');
          res = await retrieveConnection();
          if (!res) {
            connectionMessage.push('Failed to retrieve connection to database');
          }
        }
      }

      const message = connectionMessage.join('|');
      if (message !== lastConnectionLog) {
        lastConnectionLog = message;
        logTransaction(LogType.Internal, `[SQLITE]: ${message}`);
      }

      if (!res) {
        throw new Error(message);
      }

      return db;
    } catch (ex) {
      const message = connectionMessage.join('|');
      lastConnectionLog = message;
      logTransaction(LogType.Error, `[SQLITE]: ${message}`);
      throw ex;
    }
  },
  run: async (query: string, values: any[] = []): Promise<number> => {
    sqlConnectionProvider.getConnection();

    // Only run one update at a time. An issue in Android was identified where if multiple
    // updates are running at the same time, the db.close() will close all connections.
    // Do we even need to close the connection or should we open it once and leave it open?

    if (runQueriesInQueue === 0) {
      runQueryNumber = 0;
      runQuerySubject.next(0);
    }

    runQueriesInQueue++;
    const nextRunQueryNumber = ++runQueryNumber;

    try {
      // Only queue if more than 1 concurrent requests
      if (runQueriesInQueue >= 2) {
        logConsole(LogType.Warn, `[SQLITE]: ${runQueriesInQueue} QUERIES IN QUEUE. WAITING FOR SUBJECT ID ${nextRunQueryNumber - 1}`);
        await firstValueFrom(
          runQuerySubject.pipe(
            filter((res) => res === nextRunQueryNumber - 1),
            take(1),
          ),
        );
        logConsole(LogType.Info, `[SQLITE]: SUBJECT ID ${nextRunQueryNumber - 1} DETECTED!`);
      }

      logConsole(LogType.Info, `[SQLITE]\n${query}`);
      //logConsole(LogType.Info, `[SQLITE]: PARAMS [${values.join(',')}]`);

      let db: SQLiteDBConnection;
      let isConnected = await sqlite.isConnection(dbName);
      if (isConnected.result) {
        db = await sqlite.retrieveConnection(dbName);
      } else {
        db = await sqlite.createConnection(dbName, false, 'no-encryption', 1);
      }

      await db.open();
      logConsole(LogType.Info, `[SQLITE]: RUN START`);
      const result: any = await db.run(query, values);
      const changes: number = result.changes.changes;
      logConsole(LogType.Info, `[SQLITE]: RUN COMPLETE (${changes} changes)`);
      await db.close();

      return changes;
    } catch (ex) {
      logTransaction(LogType.Error, `[SQLITE]: ERROR (${ex})`);
      return 0;
    } finally {
      runQueriesInQueue--;
      runQuerySubject.next(nextRunQueryNumber);
    }
  },
  query: async (query: string): Promise<capSQLiteValues | null> => {
    try {
      logConsole(LogType.Info, `[SQLITE]\n${query}`);

      let db: SQLiteDBConnection;
      let isConnected = await sqlite.isConnection(dbName);
      if (isConnected.result) {
        db = await sqlite.retrieveConnection(dbName);
      } else {
        db = await sqlite.createConnection(dbName, false, 'no-encryption', 1);
      }

      await db.open();
      logConsole(LogType.Info, `[SQLITE]: QUERY START`);
      const result = await db.query(query);
      logConsole(LogType.Info, `[SQLITE]: QUERY COMPLETE`);
      await db.close();
      //await sqlite.closeConnection(dbName);

      return result;
    } catch (ex) {
      logTransaction(LogType.Error, `[SQLITE]: ERROR (${ex})`);
      return null;
    }
  },
  storageSet: async (key: string, value: string) => {
    try {
      await storage?.set(key, value);
    } catch (ex: any) {
      let valueOutput = value;
      if (valueOutput.length >= 20) {
        valueOutput = `${valueOutput.substring(0, 20)}...`;
      }
      logConsole(LogType.Error, `[STORAGE]: ERROR SETTING ${key} to ${valueOutput} | ${ex}`);
    }
  },
  storageGet: async (key: string) => {
    try {
      const res = (await storage?.get(key)) as string;
      return res;
    } catch (ex) {
      logConsole(LogType.Error, `[STORAGE]: ERROR GETTING ${key} | ${ex}`);
    }
  },
  storageRemove: async (key: string) => {
    try {
      await storage?.remove(key);
    } catch (ex) {
      logConsole(LogType.Error, `[STORAGE]: ERROR REMOVING ${key} | ${ex}`);
    }
  },
};

interface IComponentProps {
  databaseName: string;
  children: ReactNode;
}

const SqlConnectionProvider: React.FC<IComponentProps> = function ({ databaseName, children }) {
  dbName = databaseName;
  sqlite = useSQLite();
  const [presentAlert] = useIonAlert();

  const [hasLoadedData, setHasLoadedData] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchData = async () => {
      await initNativeStorage();
      await loadTransactionLog(dispatch);
      await logTransaction(LogType.Internal, '[SQLITE]: sqlConnectionProvider useEffect()');
      const wasUsingSQLite = (await sqlConnectionProvider.storageGet(ConfigName.IsUsingSQLite)) === '1' ? true : false;
      const sqliteTestSuccess = await runSqliteTests();
      await initDb();
      await loadData(dispatch);
      setHasLoadedData(true);

      if (!sqliteTestSuccess && wasUsingSQLite) {
        try {
          await logTransaction(LogType.Error, '[SQLITE]: Device no longer using SQLite');
        } catch {}
      }

      // A little hack to help remove the loading spinners in development.
      // This is a bug caused by the <React.StrictMode> tag doing a double render.
      // TODO: fix this a better way
      if (process.env.REACT_APP_PRODUCTION === 'false') {
        var loadings = document.getElementsByTagName('ion-loading');
        for (var k = loadings.length - 1; k >= 0; k--) {
          var item = loadings[k];
          item.remove();
        }
      }
    };
    if (runOnce <= 0) {
      runOnce++;
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initDb = async () => {
    logTransaction(LogType.Internal, '[SQLITE]: initialising database');
    try {
      await upgradeVersion0000();
    } catch {}
    await upgradeVersion0001();
    try {
      await upgradeVersion0002();
    } catch {}
    await upgradeVersion0003();
    try {
      await upgradeVersion0004();
    } catch {}
  };

  const loadData = async (dispatch: AppDispatch) => {
    logTransaction(LogType.Internal, '[SQLITE]: loading data');
    await loadConfig(dispatch);
  };

  const initNativeStorage = async () => {
    const store = new Storage({
      name: dbName,
      driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
    });
    storage = await store.create();
  };

  const runSqliteTests = async () => {
    const wasUsingSQLite = (await sqlConnectionProvider.storageGet(ConfigName.IsUsingSQLite)) === '1' ? true : false;
    isUsingSQLiteRef = wasUsingSQLite;

    try {
      await sqlConnectionProvider.getConnection();
      await updateConfigBool(ConfigName.IsUsingSQLite, true, true);
      isUsingSQLiteRef = true;
      return true;
    } catch (ex: any) {
      isUsingSQLiteRef = false;
      // If the device has swapped from using SQLite to not using SQLite, this is a problem that must be managed.
      if (wasUsingSQLite) {
        logTransaction(LogType.Error, `[SQLITE]: WARNING SQLite usage was detected on a previous load but is no longer available!`);
        await presentAlert({
          header: 'Database Error',
          subHeader: '',
          message: 'Tech Mobile failed to connect to your device database',
          buttons: [
            {
              text: 'OK',
              role: 'cancel',
            },
          ],
          onDidDismiss: () => {
            if (window.cordova) {
              (window.cordova.plugins as any).exit();
            } else {
              window.location.reload();
            }
          },
        });
      }
    }

    return false;
  };

  const loadTransactionLog = async (dispatch: AppDispatch) => {
    const logSizeString = await selectConfigString(ConfigName.TransactionLogSize, true);
    const logSize = parseInt(logSizeString || '0');

    try {
      dispatch({
        type: setTransactionLogSizeState.toString(),
        payload: logSize,
      });
    } catch (ex: any) {
      dispatch({
        type: setTransactionLogSizeState.toString(),
        payload: 0,
      });
    }
  };

  return (
    <SqlConnectionContext.Provider
      value={{
        getConnection: sqlConnectionProvider.getConnection,
        run: sqlConnectionProvider.run,
        query: sqlConnectionProvider.query,
        storageSet: sqlConnectionProvider.storageSet,
        storageGet: sqlConnectionProvider.storageGet,
        storageRemove: sqlConnectionProvider.storageRemove,
      }}>
      <LoadingMessage isOpen={!hasLoadedData} message={'loading clocking data'} />
      {hasLoadedData ? children : <></>}
    </SqlConnectionContext.Provider>
  );
};

export const SqlConnectionContext = createContext<typeof sqlConnectionProvider>(sqlConnectionProvider);
export default SqlConnectionProvider;
